import { useTranslation } from 'react-i18next';
import { Flex, Text } from '@chakra-ui/react';
import format from 'date-fns/format';

import type { ApiNabanDeed } from '@izimi/api';
import { useDateLocale, useIsMobile } from '@izimi/hooks';

import { CustomIcon } from '../../../../icon';
import { NabanCardListItem } from '../../../components/NabanCardListItem';
import { NabanDetailsListCard } from '../../../components/NabanDetailsListCard';
import { NabanDetailsListItem } from '../../../components/NabanDetailsListItem';
import { useNabanStoreSelectedAuthenticationMethod } from '../../../store';
import type { RenderDeedOptions } from '../../util/types';
import { nabanDetailsListCardContentProps } from './styles';

type Props = {
  documents: ApiNabanDeed['financeFeedbackDocuments'];
  napId: string;
  renderDeedOptions?: RenderDeedOptions;
};

export const NabanDeedFinancesFeedbackDocuments = ({
  documents,
  napId,
  renderDeedOptions,
}: Props) => {
  const isMobile = useIsMobile();
  const {
    t,
    i18n: { language },
  } = useTranslation('naban');
  const locale = useDateLocale(language);

  const selectedAuthenticationMethod = useNabanStoreSelectedAuthenticationMethod();

  return (
    <NabanDetailsListCard
      title={t('deed.feedbackDocuments.title')}
      contentProps={nabanDetailsListCardContentProps}>
      {documents.map((doc) => (
        <Flex key={doc.documentToken} w='full' flexDirection='column'>
          <NabanCardListItem title={doc.documentType?.label ?? ''}>
            {doc.officeName && (
              <NabanDetailsListItem
                flex={1}
                title={t('deed.feedbackDocuments.label.receivedBy')}
                value={doc.officeName}
                display={isMobile ? 'none' : 'flex'}
              />
            )}
            {doc.registrationReference && (
              <NabanDetailsListItem
                flex={1}
                title={t('deed.feedbackDocuments.label.reference')}
                value={doc.registrationReference}
                display={isMobile ? 'none' : 'flex'}
              />
            )}
            {doc.registrationDate && (
              <NabanDetailsListItem
                flex={1}
                title={t('deed.feedbackDocuments.label.registeredOn')}
                value={format(new Date(doc.registrationDate), 'PPP', { locale })}
                display={{
                  base: 'none',
                  xl: 'flex',
                }}
              />
            )}
            {renderDeedOptions
              ? renderDeedOptions({
                  documentToken: doc.documentToken,
                  documentTitle: doc.documentType?.label ?? '',
                  napId,
                  documentIdentifier: doc.documentIdentifier,
                  authenticationModeToken: selectedAuthenticationMethod?.token,
                  isEmbeddedDocument: false,
                })
              : null}
          </NabanCardListItem>
          {doc.embeddedDocumentToken && (
            <Flex gap={4} alignItems='center'>
              <CustomIcon name='cornerDownRight' color='neutral600' w='28px' />
              <Text variant='tableCell' fontSize={18} color='neutral600' flexGrow={1}>
                {t('deed.feedbackDocuments.label.feedbackDocument')}
              </Text>
              {renderDeedOptions
                ? renderDeedOptions({
                    documentToken: doc.embeddedDocumentToken,
                    documentTitle: doc.documentType?.label ?? '',
                    napId,
                    documentIdentifier: doc.documentIdentifier,
                    authenticationModeToken: selectedAuthenticationMethod?.token,
                    isEmbeddedDocument: true,
                  })
                : null}
            </Flex>
          )}
        </Flex>
      ))}
    </NabanDetailsListCard>
  );
};
